// database.structure.ts
import Dexie, { Table } from 'dexie';
import { deliveryStateOf } from '../helpers/deliveryStateHelpers';
import {
  AssetManufacturerModel,
  AssetModel,
  AssetModelModel,
  AssetTypeModel,
  CustomerModel,
  DataTypeModel,
  DimensionTypeModel,
  EquipmentCategoryModel,
  EquipmentModel,
  EquipmentPartModel,
  EquipmentTypeModel,
  InspectionCategoryModel,
  InspectionModel,
  InspectionStatusModel,
  InspectionTypeModel,
  MineLocationModel,
  ObservableActionTypeModel,
  ObservableItemActionTypeModel,
  ObservableItemModel,
  ObservableLevelModel,
  PartCategoryModel,
  PartDimensionModel,
  PartDimensionThresholdModel,
  PartLocationModel,
  PartManufacturerModel,
  PartModel,
  PartTypeModel,
  SiteDiggingConditionModel,
  UnitModel,
  ObservationModel,
  TooltipModel,
  EquipmentConfigurationModel,
  SiteModel,
  EquipmentPartConfigurationModel,
  PartTypeMeasurementToolModel,
  UndercarriageChangeOutModel,
} from './types';

export class InspectDatabase extends Dexie {
  inspections!: Table<InspectionModel, string>;
  inspectionBackups!: Table<InspectionModel, string>;
  observableItems!: Table<ObservableItemModel, number>;
  assets!: Table<AssetModel, number>;
  tooltips!: Table<TooltipModel, string>;
  //Lookups
  assetManufacturers!: Table<AssetManufacturerModel, number>;
  assetModels!: Table<AssetModelModel, number>;
  assetTypes!: Table<AssetTypeModel, number>;
  customers!: Table<CustomerModel, number>;
  dataTypes!: Table<DataTypeModel, number>;
  dimensionTypes!: Table<DimensionTypeModel, number>;
  equipmentCategories!: Table<EquipmentCategoryModel, number>;
  equipmentConfigurations!: Table<EquipmentConfigurationModel, number>;
  equipmentPartConfigurations!: Table<EquipmentPartConfigurationModel, number>;
  equipments!: Table<EquipmentModel, string>;
  equipmentParts!: Table<EquipmentPartModel, string>;
  equipmentTypes!: Table<EquipmentTypeModel, number>;
  inspectionCategories!: Table<InspectionCategoryModel, number>;
  inspectionStatuses!: Table<InspectionStatusModel, number>;
  inspectionTypes!: Table<InspectionTypeModel, number>;
  mineLocations!: Table<MineLocationModel, number>;
  observableActionTypes!: Table<ObservableActionTypeModel, number>;
  observableItemActionTypes!: Table<ObservableItemActionTypeModel, number>;
  observableLevels!: Table<ObservableLevelModel, number>;
  partCategories!: Table<PartCategoryModel, number>;
  parts!: Table<PartModel, string>;
  partLocations!: Table<PartLocationModel, number>;
  partManufacturers!: Table<PartManufacturerModel, number>;
  partTypes!: Table<PartTypeModel, number>;
  partTypeMeasurementTools!: Table<PartTypeMeasurementToolModel, number>;
  partDimensions!: Table<PartDimensionModel, string>;
  partDimensionThresholds!: Table<PartDimensionThresholdModel, number>;
  sites!: Table<SiteModel, number>;
  siteDiggingConditions!: Table<SiteDiggingConditionModel, number>;
  units!: Table<UnitModel, number>;
  undercarriageChangeOuts!: Table<UndercarriageChangeOutModel, number>;

  constructor(name = 'inspect_db_14') {
    super(name);

    this.version(1).stores({
      inspections:
        '&externalId, id, inspectionStatus, assetId, siteId, inspectionDate, completed, mineLocationId, equipmentExternalId',
      inspectionBackups:
        '&externalId, id, inspectionStatus, assetId, siteId, inspectionDate, completed',
      observableItems: '&id, identifier, dataType',
      assets:
        '&id, name, modelId, assetTypeId, manufacturerId, siteId, parentAssetId, customerId',
      tooltips: '&key, heading',
      //Lookups
      assetManufacturers: '&id, name',
      assetModels: '&id, name, manufacturerId, assetTypeId',
      assetTypes: '&id, name',
      customers: '&id, name',
      dataTypes: '&id, name',
      dimensionTypes: '&id, name, unitId',
      equipmentCategories: '&id, name',
      equipmentConfigurations: '&id, name',
      equipmentPartConfigurations:
        '&id, configurationId, partId, position, locationId, secondaryLocationId',
      equipments:
        '&externalId, id, assetId, equipmentTypeId, name, mineLocationId',
      equipmentParts:
        '&externalId, id, equipmentId, externalEquipmentId, partId, [externalEquipmentId+partId]',
      equipmentTypes: '&id, equipmentCategoryId, name',
      inspectionCategories: '&id, name',
      inspectionStatuses: '&id, name',
      inspectionTypes: '&id, name',
      mineLocations: '&externalId, id, siteId, name',
      observableActionTypes: '&id, name, sortOrder',
      observableItemActionTypes: '&id, observableItemId, actionTypeId',
      observableLevels: '&id, name',
      partCategories: '&id, name',
      parts: '&externalId, id, name, partNumber, partTypeId, manufacturerId',
      partLocations: '&id, name',
      partManufacturers: '&id, name',
      partTypeMeasurementTools: '&id, partTypeId, measurementToolId',
      partTypes: '&id, categoryId, name',
      partDimensions:
        '&externalId, id, partId, dimensionTypeId, observableItemId, [observableItemId+partId]',
      partDimensionThresholds:
        '&id, partDimensionId, minPercentage, actionTypeId',
      sites: '&id, name, customerId',
      siteDiggingConditions: '&id, name',
      units: '&id, code, name',
    });

    // #10223 native adoption: the typed undercarriage change-out read model, down-synced
    // from events.UndercarriageChangeOut via the fleet sync. An in-place version bump (same
    // database name) so every unsynced inspection survives the PWA update untouched.
    this.version(2).stores({
      undercarriageChangeOuts:
        '&id, undercarriageSideEquipmentId, changeOutDate',
    });

    // #11657: the pending count and background sync read the delivery state through an
    // index instead of every stored Inspection.
    this.version(3)
      .stores({
        inspections:
          '&externalId, id, inspectionStatus, assetId, siteId, inspectionDate, completed, mineLocationId, equipmentExternalId, deliveryState',
      })
      .upgrade((transaction) =>
        // Set the field explicitly here so the backfill of existing Inspections does not
        // depend on whether the write middleware below runs inside an upgrade transaction.
        transaction
          .table<InspectionModel>('inspections')
          .toCollection()
          .modify((inspection) => {
            inspection.deliveryState = deliveryStateOf(inspection);
          }),
      );

    // Set on every write, so the indexed state can't drift from the record it describes.
    this.use({
      stack: 'dbcore',
      name: 'inspectionDeliveryState',
      create: (down) => ({
        ...down,
        table: (tableName) => {
          const table = down.table(tableName);
          if (tableName !== 'inspections') return table;
          return {
            ...table,
            mutate: (request) =>
              request.type === 'add' || request.type === 'put'
                ? table.mutate({
                    ...request,
                    values: request.values.map(
                      (inspection: InspectionModel) => ({
                        ...inspection,
                        deliveryState: deliveryStateOf(inspection),
                      }),
                    ),
                  })
                : table.mutate(request),
          };
        },
      }),
    });
  }
}

export const inspectionDB = new InspectDatabase();
